import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { SystemSeismograph } from '../components/SystemSeismograph';
export function WorkSnapshotOutput() {
  // In production, this would come from API based on user inputs
  const snapshotData = {
    primaryPattern: 'Over-compensation under authority pressure',
    patternDescription: 'When authority dynamics or performance pressure activates, you over-deliver or over-explain to manage perceived risk. This creates temporary relief but reinforces the pattern that your baseline output is insufficient.',
    triggerContext: 'This pattern activates most strongly in situations involving evaluation, visibility to leadership, or high-stakes deliverables.',
    nextSteps: ['This is a surface-level orientation. For deeper work pattern analysis, consider the Work Self Decode.', 'If this involves a specific person (boss, client, leader), the Work Relationship Decode maps the dynamic.', 'Pattern interruption requires recognizing the moment between pressure and response.']
  };
  // Work snapshot data - Reactive spikes
  const systemPatternData = [{
    x: 0,
    y: 20
  }, {
    x: 25,
    y: 30
  }, {
    x: 50,
    y: 90
  }, {
    x: 75,
    y: 40
  }, {
    x: 100,
    y: 20
  }];
  return <>
      <Helmet data-id="element-5542">
        <title data-id="element-5543">Quick Decode — Work | Lazy Monk Labs</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-5544" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-5545">
        {/* Navigation */}
        <header data-id="element-5546">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" role="navigation" aria-label="Main navigation" data-id="element-5547">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-5548">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" aria-label="Return to Lazy Monk Labs home" data-id="element-5549">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-5550" />
                <span className="text-lg font-medium tracking-tight" data-id="element-5551">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-5552">
                <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-5553">
                  Decode
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" aria-label="Contact us on WhatsApp" data-id="element-5554">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        {/* Main Content */}
        <main className="pt-32 pb-24 px-6" data-id="element-5555">
          <div className="max-w-3xl mx-auto" data-id="element-5556">
            {/* Page Header */}
            <div className="mb-16" data-id="element-5557">
              <h1 className="text-4xl md:text-5xl font-medium mb-3 leading-tight tracking-tight" data-id="element-5558">
                Your Quick Decode — Work
              </h1>
              <p className="text-sm text-[#F5F5F5]/50" data-id="element-5559">
                Surface-level work pattern orientation
              </p>
            </div>

            {/* System Map Visualization */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-5560">
              <div className="mb-6" data-id="element-5561">
                <h2 className="text-xl font-medium mb-2" data-id="element-5562">
                  Pattern Intensity Map
                </h2>
                <p className="text-sm text-[#F5F5F5]/60" data-id="element-5563">
                  Visualizing where your system spikes under pressure.
                </p>
              </div>
              <SystemSeismograph data={systemPatternData} label="Pressure Response Map" data-id="element-5564" />
            </section>

            {/* Primary Pattern */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-5565">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-5566">
                Primary work pattern
              </h2>
              <p className="text-xl text-[#F5F5F5]/80 mb-6 font-medium" data-id="element-5567">
                {snapshotData.primaryPattern}
              </p>
              <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-5568">
                {snapshotData.patternDescription}
              </p>
            </section>

            {/* Trigger Context */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-5569">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-5570">
                When this activates
              </h2>
              <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-5571">
                {snapshotData.triggerContext}
              </p>
            </section>

            {/* Next Steps */}
            <section className="mb-12" data-id="element-5572">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-5573">
                What this means
              </h2>
              <ul className="space-y-4" data-id="element-5574">
                {snapshotData.nextSteps.map((step, index) => <li key={index} className="flex gap-4" data-id="element-5575">
                    <span className="text-[#F5F5F5]/30 mt-1" data-id="element-5576">•</span>
                    <span className="text-[#F5F5F5]/70 leading-relaxed flex-1" data-id="element-5577">
                      {step}
                    </span>
                  </li>)}
              </ul>
            </section>

            {/* CTA Section */}
            <section className="border border-[#2B2B2B] p-8" data-id="element-5578">
              <h3 className="text-xl font-medium mb-4" data-id="element-5579">
                Want deeper analysis?
              </h3>
              <p className="text-[#F5F5F5]/70 mb-6 leading-relaxed" data-id="element-5580">
                This quick decode provides surface-level orientation. For
                complete work pattern mechanics, constraints, and leverage
                points, consider a full diagnostic report.
              </p>
              <Link to="/reports" className="inline-block px-8 py-4 bg-[#F5F5F5] text-[#0A0A0A] text-center font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-5581">
                View Full Reports
              </Link>
            </section>
          </div>
        </main>

        {/* Minimal Footer */}
        <footer className="py-8 px-6 border-t border-[#2B2B2B]" role="contentinfo" data-id="element-5582">
          <div className="max-w-7xl mx-auto text-center" data-id="element-5583">
            <p className="text-xs text-[#F5F5F5]/40" data-id="element-5584">
              © {new Date().getFullYear()} Lazy Monk Labs. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>;
}